import { Box, Flex, Heading, Spinner, Stack } from "@chakra-ui/react";
import { useEthers } from "@usedapp/core";
import { orderBy } from "lodash-es";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors } from "../../core/constants/colors";
import { I18N_NAMESPACE } from "../../core/constants/i18n";
import { useAccountBorrow } from "../../core/hooks/useAccountBorrow";
import { useAccountCredit } from "../../core/hooks/useAccountCredit";
import { useMarketplaceContract } from "../../core/hooks/useContract";
import { ISale } from "../../core/models/order";
import { marketplaceService } from "../../services/marketplace.service";
import { CompletedOrder } from "./components/CompletedOrder";
import { UpcomingOrder } from "./components/UpcomingOrder";

const Purchases = React.memo(() => {
  const { t } = useTranslation([I18N_NAMESPACE.PROFILE]);
  const borrowAmount = useAccountBorrow();
  const creditAmount = useAccountCredit();
  const { account, library } = useEthers();
  const marketplaceContract = useMarketplaceContract();

  const [upcomingOrders, setUpcomingOrders] = useState<ISale[]>([]);
  const [completedOrders, setCompletedOrders] = useState<ISale[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const getOrders = async () => {
    if (!account) return;

    const response = await marketplaceService.getOrders({ buyer: account.toLowerCase() });
    setCompletedOrders(
      orderBy(
        response.data.filter((i) => i.is_completed),
        "created_time",
        "desc"
      )
    );
    setUpcomingOrders(
      orderBy(
        response.data.filter((i) => !i.is_completed),
        "created_time",
        "desc"
      )
    );
    setIsLoading(false);
  };

  useEffect(() => {
    getOrders();
  }, [account]);

  return (
    <Stack spacing={8}>
      <Flex
        borderRadius="lg"
        borderWidth={1}
        width={{base:"100%", lg:"70%"}}
        alignSelf={"center"}
        margin="10px 0px">
        <Box
          p={{base:3, md:4}}
          flex={1}
          borderRadius="lg"
          background={colors.primary}
          textAlign={"center"}
          fontWeight={500}
          color={"#fff"}
          fontSize={{base:"md", md:"xl"}}
        >
          {t("Total you owe")}: <b>{(borrowAmount / 1e18).toFixed(2)}$</b>
        </Box>
        <Box py={2} flex={1} textAlign={"center"} fontWeight={500} fontSize={{base:"md", md:"xl"}}>
          {t("Credit balance")}: <b>{((creditAmount - borrowAmount) / 1e18).toFixed(2)}$</b>
        </Box>
      </Flex>

      {isLoading ? (
        <Flex alignItems={"center"} justifyContent={"center"}>
          <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="blue.500" size="xl" />
        </Flex>
      ) : (
        <>
          {upcomingOrders.length > 0 && (
            <Box>
              <Heading size="small">{t("Upcoming Payments")}</Heading>
              {upcomingOrders.map((order) => (
                <UpcomingOrder order={order} key={`order-${order.id}`} onRepaySuccess={getOrders} />
              ))}
            </Box>
          )}

          {completedOrders.length > 0 && (
            <Box>
              <Heading size="small">{t("Completed Payments")}</Heading>
              {completedOrders.map((order) => (
                <CompletedOrder order={order} key={`order-${order.id}`} />
              ))}
            </Box>
          )}
        </>
      )}
    </Stack>
  );
});

export default Purchases;
